/**
 * Rechnungen — Utility-Funktionen
 * DESIGN: Reduziert, professionell.
 * 
 * - Rechnungsnummer-Generierung pro Betrieb
 * - PDF-Generierung mit Briefkopf
 * - DATEV CSV Export
 * - Honorar-Berechnung (Gutachten)
 */

import { supabase } from './supabase';
import type { Rechnung, Rechnungsposition, BetriebDB, Honorar } from './database.types';

// ── Rechnungsnummer generieren ──
export async function generiereRechnungsnummer(businessUnit: string): Promise<string> {
  const jahr = new Date().getFullYear();
  
  // Zähler aus DB holen und inkrementieren
  const { data: zaehler, error } = await supabase
    .from('rechnungsnummern_zaehler')
    .select('*')
    .eq('business_unit', businessUnit)
    .eq('jahr', jahr)
    .single();

  if (error || !zaehler) {
    // Fallback: Timestamp-basiert
    const ts = Date.now().toString(36).toUpperCase();
    const prefixMap: Record<string, string> = {
      gutachten: 'GA', autoglas: 'AG', mietwagen: 'MW', werkstatt: 'WK',
      protex: 'PX', unfallgiganten: 'UG', airbnb: 'AB', recht: 'RA',
    };
    return `${prefixMap[businessUnit] || 'XX'}-${jahr}-${ts}`;
  }

  const neuerZaehler = (zaehler.letzter_zaehler || 0) + 1;
  
  // Zähler aktualisieren
  await supabase
    .from('rechnungsnummern_zaehler')
    .update({ letzter_zaehler: neuerZaehler })
    .eq('id', zaehler.id);

  // Format: GA-2026-0001
  return `${zaehler.prefix}-${jahr}-${String(neuerZaehler).padStart(4, '0')}`;
}

// ── Beträge berechnen ──
export function berechneBetraege(positionen: Rechnungsposition[]) {
  let netto = 0;
  let mwst = 0;

  for (const pos of positionen) {
    const posNetto = pos.menge * pos.einzelpreis;
    const posMwst = posNetto * (pos.mwst_satz / 100);
    netto += posNetto;
    mwst += posMwst;
  }

  return {
    netto: Math.round(netto * 100) / 100,
    mwst: Math.round(mwst * 100) / 100,
    brutto: Math.round((netto + mwst) * 100) / 100,
  };
}

// ── Honorar berechnen (Gutachten) ──
export async function berechneHonorar(schadenshoehe: number, vereinbarungsSlug: string): Promise<{
  grundhonorar: number;
  fotoPauschale: number;
  schreibkosten: number;
  fahrtkostenKm: number;
  portoTelefon: number;
  restwertErmittlung: number;
  wertminderung: number;
  gesamt: number;
} | null> {
  const { data: tabelle } = await supabase
    .from('honorartabelle')
    .select('*')
    .eq('aktiv', true)
    .eq('vereinbarung_slug', vereinbarungsSlug)
    .lte('schadenshoehe_von', schadenshoehe)
    .gt('schadenshoehe_bis', schadenshoehe)
    .single();

  if (!tabelle) return null;

  const h = tabelle as Honorar;
  const gesamt = h.grundhonorar + h.foto_pauschale + h.schreibkosten + h.porto_telefon + h.restwert_ermittlung + h.wertminderung;

  return {
    grundhonorar: h.grundhonorar,
    fotoPauschale: h.foto_pauschale,
    schreibkosten: h.schreibkosten,
    fahrtkostenKm: h.fahrtkosten_km,
    portoTelefon: h.porto_telefon,
    restwertErmittlung: h.restwert_ermittlung,
    wertminderung: h.wertminderung,
    gesamt,
  };
}

// ── Betriebsdaten laden ──
export async function ladeBetriebsdaten(businessUnit: string): Promise<BetriebDB | null> {
  const { data, error } = await supabase
    .from('betriebe')
    .select('*')
    .eq('typ', businessUnit)
    .single();

  if (error || !data) return null;
  return data as BetriebDB;
}

// ── PDF HTML generieren ──
export function generiereRechnungHTML(
  rechnung: Rechnung,
  positionen: Rechnungsposition[],
  betrieb: BetriebDB
): string {
  const formatEuro = (n: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
  const formatDatum = (d: string) => new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const positionenHTML = positionen.map((pos, idx) => `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #E5E7EB;text-align:center">${idx + 1}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E5E7EB">
        <strong>${pos.bezeichnung}</strong>
        ${pos.beschreibung ? `<br><span style="color:#6B7280;font-size:11px">${pos.beschreibung}</span>` : ''}
      </td>
      <td style="padding:6px 8px;border-bottom:1px solid #E5E7EB;text-align:center">${pos.menge} ${pos.einheit}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E5E7EB;text-align:right">${formatEuro(pos.einzelpreis)}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E5E7EB;text-align:right">${pos.mwst_satz}%</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E5E7EB;text-align:right"><strong>${formatEuro(pos.menge * pos.einzelpreis)}</strong></td>
    </tr>
  `).join('');

  return `<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <style>
    @page { size: A4; margin: 20mm 15mm 25mm 15mm; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #1F2937; line-height: 1.5; }
    .briefkopf { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 15px; border-bottom: 2px solid #9B1C1C; }
    .firma-info { text-align: right; font-size: 10px; color: #6B7280; line-height: 1.6; }
    .firma-name { font-size: 16px; font-weight: 700; color: #1F2937; margin-bottom: 4px; }
    .empfaenger { margin-bottom: 25px; }
    .empfaenger-label { font-size: 8px; color: #9CA3AF; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 2px; }
    .rechnungs-info { display: flex; justify-content: space-between; margin-bottom: 20px; }
    .info-block { }
    .info-label { font-size: 9px; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.5px; }
    .info-value { font-size: 13px; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th { background: #F3F4F6; padding: 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #6B7280; border-bottom: 2px solid #E5E7EB; }
    .summen { margin-top: 15px; margin-left: auto; width: 280px; }
    .summen-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 12px; }
    .summen-row.total { border-top: 2px solid #1F2937; padding-top: 8px; margin-top: 4px; font-size: 14px; font-weight: 700; }
    .zahlungshinweis { margin-top: 30px; padding: 12px; background: #F9FAFB; border-radius: 6px; font-size: 11px; }
    .fusszeile { position: fixed; bottom: 0; left: 0; right: 0; padding: 10px 15mm; font-size: 8px; color: #9CA3AF; border-top: 1px solid #E5E7EB; display: flex; justify-content: space-between; }
  </style>
</head>
<body>
  <!-- Briefkopf -->
  ${betrieb.briefkopf_html ? betrieb.briefkopf_html : `
  <div class="briefkopf">
    <div>
      ${betrieb.logo_url ? `<img src="${betrieb.logo_url}" alt="Logo" style="height:50px;margin-bottom:8px">` : ''}
      <div class="firma-name">${betrieb.firma}</div>
    </div>
    <div class="firma-info">
      ${betrieb.strasse || ''}<br>
      ${betrieb.plz || ''} ${betrieb.ort || ''}<br>
      Tel: ${betrieb.telefon || ''}<br>
      ${betrieb.email || ''}<br>
      ${betrieb.website || ''}
    </div>
  </div>
  `}

  <!-- Empfänger -->
  <div class="empfaenger">
    <div class="empfaenger-label">Empfänger</div>
    ${rechnung.empfaenger_firma ? `<strong>${rechnung.empfaenger_firma}</strong><br>` : ''}
    ${rechnung.empfaenger_name || ''}<br>
    ${rechnung.empfaenger_strasse || ''}<br>
    ${rechnung.empfaenger_plz || ''} ${rechnung.empfaenger_ort || ''}
  </div>

  <!-- Rechnungsinfos -->
  <div class="rechnungs-info">
    <div class="info-block">
      <div class="info-label">Rechnungsnummer</div>
      <div class="info-value">${rechnung.rechnungs_nr}</div>
    </div>
    <div class="info-block">
      <div class="info-label">Rechnungsdatum</div>
      <div class="info-value">${formatDatum(rechnung.rechnungsdatum)}</div>
    </div>
    ${rechnung.leistungsdatum ? `
    <div class="info-block">
      <div class="info-label">Leistungsdatum</div>
      <div class="info-value">${formatDatum(rechnung.leistungsdatum)}</div>
    </div>` : ''}
    ${rechnung.faellig_am ? `
    <div class="info-block">
      <div class="info-label">Fällig am</div>
      <div class="info-value">${formatDatum(rechnung.faellig_am)}</div>
    </div>` : ''}
  </div>

  ${rechnung.kennzeichen || rechnung.schadensnummer ? `
  <div style="margin-bottom:15px;padding:8px 12px;background:#F9FAFB;border-radius:4px;font-size:11px">
    ${rechnung.kennzeichen ? `<strong>Kennzeichen:</strong> ${rechnung.kennzeichen} &nbsp;&nbsp;` : ''}
    ${rechnung.fahrzeug ? `<strong>Fahrzeug:</strong> ${rechnung.fahrzeug} &nbsp;&nbsp;` : ''}
    ${rechnung.schadensnummer ? `<strong>Schadensnr.:</strong> ${rechnung.schadensnummer} &nbsp;&nbsp;` : ''}
    ${rechnung.versicherung ? `<strong>Versicherung:</strong> ${rechnung.versicherung}` : ''}
  </div>` : ''}

  <!-- Betreff -->
  <h2 style="font-size:15px;margin-bottom:5px">${rechnung.betreff}</h2>

  <!-- Positionen -->
  <table>
    <thead>
      <tr>
        <th style="width:40px;text-align:center">Pos.</th>
        <th style="text-align:left">Bezeichnung</th>
        <th style="width:80px;text-align:center">Menge</th>
        <th style="width:90px;text-align:right">Einzelpreis</th>
        <th style="width:60px;text-align:right">MwSt.</th>
        <th style="width:100px;text-align:right">Gesamt</th>
      </tr>
    </thead>
    <tbody>
      ${positionenHTML}
    </tbody>
  </table>

  <!-- Summen -->
  <div class="summen">
    <div class="summen-row">
      <span>Nettobetrag</span>
      <span>${formatEuro(rechnung.netto_betrag)}</span>
    </div>
    <div class="summen-row">
      <span>MwSt. ${rechnung.mwst_satz}%</span>
      <span>${formatEuro(rechnung.mwst_betrag)}</span>
    </div>
    <div class="summen-row total">
      <span>Gesamtbetrag</span>
      <span>${formatEuro(rechnung.brutto_betrag)}</span>
    </div>
  </div>

  <!-- Zahlungshinweis -->
  <div class="zahlungshinweis">
    <strong>Zahlungshinweis:</strong> Bitte überweisen Sie den Betrag innerhalb von 14 Tagen auf folgendes Konto:<br>
    <strong>${betrieb.bank || ''}</strong> · IBAN: ${betrieb.iban || ''} · BIC: ${betrieb.bic || ''}<br>
    Verwendungszweck: ${rechnung.rechnungs_nr}
    ${rechnung.anwalt_beteiligt && rechnung.anwalt_name ? `<br><br><strong>Anwaltliche Vertretung:</strong> ${rechnung.anwalt_name}` : ''}
  </div>

  ${rechnung.notizen ? `<div style="margin-top:15px;font-size:11px;color:#6B7280"><strong>Hinweis:</strong> ${rechnung.notizen}</div>` : ''}

  <!-- Fußzeile -->
  ${betrieb.fusszeile_html ? betrieb.fusszeile_html : `
  <div class="fusszeile">
    <span>${betrieb.firma} · ${betrieb.strasse || ''} · ${betrieb.plz || ''} ${betrieb.ort || ''}</span>
    <span>St.-Nr.: ${betrieb.steuernummer || ''} ${betrieb.ust_id ? `· USt-IdNr.: ${betrieb.ust_id}` : ''}</span>
    <span>${betrieb.iban || ''}</span>
  </div>
  `}
</body>
</html>`;
}

// ── DATEV CSV Export ──
export interface DatevBuchung {
  umsatz: number;
  sollHaben: 'S' | 'H';
  konto: string;
  gegenkonto: string;
  belegdatum: string;
  buchungstext: string;
  belegnummer: string;
  kostenstelle?: string;
}

export function generiereeDatevCSV(buchungen: DatevBuchung[]): string {
  // DATEV CSV Format (vereinfacht, kompatibel mit DATEV Unternehmen Online)
  const header = [
    'Umsatz (ohne Soll/Haben-Kz)',
    'Soll/Haben-Kennzeichen',
    'WKZ Umsatz',
    'Konto',
    'Gegenkonto (ohne BU-Schlüssel)',
    'BU-Schlüssel',
    'Belegdatum',
    'Belegfeld 1',
    'Belegfeld 2',
    'Skonto',
    'Buchungstext',
    'Postensperre',
    'Diverse Adressnummer',
    'Geschäftspartnerbank',
    'Sachverhalt',
    'Zinssperre',
    'Beleglink',
    'Beleginfo - Art 1',
    'Beleginfo - Inhalt 1',
    'Kostenstelle',
  ].join(';');

  const rows = buchungen.map(b => {
    const umsatz = b.umsatz.toFixed(2).replace('.', ',');
    const datum = formatDatevDatum(b.belegdatum);
    return [
      umsatz,
      b.sollHaben,
      'EUR',
      b.konto,
      b.gegenkonto,
      '',
      datum,
      b.belegnummer,
      '',
      '',
      b.buchungstext,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      b.kostenstelle || '',
    ].join(';');
  });

  return [header, ...rows].join('\n');
}

function formatDatevDatum(dateStr: string): string {
  const d = new Date(dateStr);
  const tag = String(d.getDate()).padStart(2, '0');
  const monat = String(d.getMonth() + 1).padStart(2, '0');
  return `${tag}${monat}`;
}

// ── DATEV Buchungskonto-Mapping ──
export const DATEV_KONTEN: Record<string, { erloes: string; forderung: string; kostenstelle: string }> = {
  gutachten: { erloes: '8400', forderung: '10000', kostenstelle: 'KST-GA' },
  autoglas: { erloes: '8401', forderung: '10100', kostenstelle: 'KST-AG' },
  mietwagen: { erloes: '8402', forderung: '10200', kostenstelle: 'KST-MW' },
  werkstatt: { erloes: '8403', forderung: '10300', kostenstelle: 'KST-WK' },
  protex: { erloes: '8404', forderung: '10400', kostenstelle: 'KST-PX' },
  unfallgiganten: { erloes: '8405', forderung: '10500', kostenstelle: 'KST-UG' },
  airbnb: { erloes: '8406', forderung: '10600', kostenstelle: 'KST-AB' },
  recht: { erloes: '8407', forderung: '10700', kostenstelle: 'KST-RA' },
};

// ── Rechnungen zu DATEV-Buchungen konvertieren ──
export function rechnungenZuDatevBuchungen(rechnungen: Rechnung[]): DatevBuchung[] {
  return rechnungen.map(r => {
    const konten = DATEV_KONTEN[r.business_unit] || DATEV_KONTEN.gutachten;
    return {
      umsatz: r.brutto_betrag,
      sollHaben: 'S' as const,
      konto: konten.forderung,
      gegenkonto: konten.erloes,
      belegdatum: r.rechnungsdatum,
      buchungstext: `${r.rechnungs_nr} ${r.betreff}`.substring(0, 60),
      belegnummer: r.rechnungs_nr,
      kostenstelle: konten.kostenstelle,
    };
  });
}

// ── Euro-Formatierung ──
export const formatEuro = (n: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
export const formatDatum = (d: string) => new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
